import { defineStore, type Store } from "pinia";
import joi from "joi";
import md5 from "md5";
import type {
  IDefaultState,
  IDefaultStoreActions,
  IDefultStoreGetters,
} from "@/interfaces";
import type { StoreName } from "@/types";
import helpers from "@/helpers";
import { globali18n } from "@/i18n";
import type { EmployeeTitleFragment } from "@/graphql";
import type { Ref } from "vue";
import { useEmployeeTitles } from "@/composables/useEmployeeTitles";

interface ITitleItem {
  id?: string;
  title: string;
  description: string;
}

export interface IState extends IDefaultState {
  item: ITitleItem;
  employeeTitles: Ref<EmployeeTitleFragment[]>;
}

export interface IStoreActions extends IDefaultStoreActions {
  isTitleNotValid(id?: string): Promise<boolean>;
}

export type EmployeeTitlesStore = Store<
  string,
  IState,
  IDefultStoreGetters<IState>,
  IStoreActions
>;

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): IState => {
      // We should fetch the data via composable. So we can use the same data
      // in everywhere.
      const { employeeTitles } = useEmployeeTitles();

      return {
        tabErrors: {},
        originalHash: null,
        validation: null,
        item: {
          title: "",
          description: "",
        },
        employeeTitles,
      };
    },
    actions: {
      currentHash(): string {
        const data = {
          ...this.getItem(),
        };
        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        this.originalHash = this.currentHash();
      },
      getValidationSchema(id?: string) {
        return joi.object({
          title: joi
            .string()
            .max(50)
            .required()
            .custom((value: string, helper: any) => {
              const exists = this.employeeTitles.some(
                (employeeTitle) =>
                  employeeTitle.title.toLowerCase() === value.toLowerCase() &&
                  id !== employeeTitle.id,
              );

              if (!exists) {
                return true;
              }

              const { t } = globali18n;
              return helper.message(t("ourhome.employees.error_title_exists"));
            }),
          description: joi.string().max(400).optional(),
        });
      },
      async setItem(data: EmployeeTitleFragment) {
        this.item = {
          id: data.id,
          title: data.title,
          description: data.description || "",
        };

        this.setOriginalHash();
        this.validation = null;
      },
      getItem() {
        const item = { ...this.item };

        return {
          id: item?.id?.toString(),
          title: item.title,
          description: item.description,
        };
      },
      async isTitleNotValid(id?: string) {
        const schema = this.getValidationSchema(id);

        this.validation = helpers.forms.validate(schema, {
          title: this.item.title,
        });

        return !this.validation.isValid;
      },
    },
  });
};

// View store
export const useEmployeeTitlesStore = createUseStore("EMPLOYEES_TITLES");

// We use the same store structure for the create modal.
export const useAddEmployeeTitlesStore = createUseStore("EMPLOYEES_ADD_TITLES");
